import { createSlice } from '@reduxjs/toolkit';
import { ErrorType, RootState } from '../../../app/store';
import { OwnerSearchResponseV2Dto, OwnerV2Dto } from '@inosio/payments-sdk';

interface OwnersDataPage {
  Rows: Array<OwnerV2Dto>;
}

type OwnerState = {
  Owners: OwnerSearchResponseV2Dto;
  OwnersDataPages: Array<OwnersDataPage>;
  isFetchingOwners: Boolean;
  isFetchingOwnersFailed: Boolean;
  isFetchingOwnersSuccess: Boolean;
  errorMessage: ErrorType;
  isCreatingOwner: Boolean;
  isCreatingOwnerSuccess: Boolean;
  isCreatingOwnerFailed: Boolean;
  createdOwner: OwnerV2Dto;
  isUpdatingOwner: boolean;
  isUpdatingOwnerSuccess: Boolean;
  isUpdatingOwnerFailed: Boolean;
  OwnerDetails: OwnerV2Dto;
  isFetchingOwnerDetails: Boolean;
  isFetchingOwnerDetailsFailed: Boolean;
  isFetchingOwnerDetailsSuccess: Boolean;
  isDownloading: Boolean;
  isDownloadingFailed: Boolean;
  isDownloadingSuccess: Boolean;
  isUploadingOwnerDocuments: Boolean;
  isUploadingOwnerDocumentsFailed: Boolean;
  isUploadingOwnerDocumentsSuccess: Boolean;
  isFetchingOwnerDocumentURL: boolean;
  isFetchingOwnerDocumentURLSuccess: boolean;
  isFetchingOwnerDocumentURLFailed: boolean;
  ownerDocumentURL: string;
  errorFetchingOwnerDocumentURL: ErrorType | null;
};

const getInitialState = () => {
  return {
    Owners: {
      StartIndex: 0,
      RangeSize: 100,
      TotalCount: 0,
      Rows: [],
    } as OwnerSearchResponseV2Dto,
    OwnersDataPages: [],
    isFetchingOwners: false,
    isFetchingOwnersFailed: false,
    isFetchingOwnersSuccess: false,
    errorMessage: {} as ErrorType,
    isCreatingOwner: false,
    isCreatingOwnerSuccess: false,
    isCreatingOwnerFailed: false,
    createdOwner: {} as OwnerV2Dto,
    isUpdatingOwner: false,
    isUpdatingOwnerSuccess: false,
    isUpdatingOwnerFailed: false,
    OwnerDetails: {} as OwnerV2Dto,
    isFetchingOwnerDetails: false,
    isFetchingOwnerDetailsFailed: false,
    isFetchingOwnerDetailsSuccess: false,
    isDownloading: false,
    isDownloadingFailed: false,
    isDownloadingSuccess: false,
    isUploadingOwnerDocuments: false,
    isUploadingOwnerDocumentsFailed: false,
    isUploadingOwnerDocumentsSuccess: false,
    isFetchingOwnerDocumentURL: false,
    isFetchingOwnerDocumentURLSuccess: false,
    isFetchingOwnerDocumentURLFailed: false,
    errorFetchingOwnerDocumentURL: null,
    ownerDocumentURL: '',
  } as OwnerState;
};

const OwnersSlice = createSlice({
  name: 'Owners',
  initialState: getInitialState(),
  reducers: {
    getFetchingOwnersData(state, action) {
      state.errorMessage = {} as ErrorType;
      state.isFetchingOwnersFailed = false;
      state.isFetchingOwners = true;
    },
    setFetchingOwnersData(state, action) {
      const _payload = action.payload;

      const recordStart = _payload.StartIndex;
      const rangeSize = _payload.RangeSize;

      let currentOwnerPages = [...state.OwnersDataPages];
      if (recordStart === 0) currentOwnerPages = [];

      const whatPageResultsIsThis = Math.ceil(recordStart / rangeSize);
      currentOwnerPages[whatPageResultsIsThis] = {
        Rows: _payload.Rows,
      };
      const ownersData = {
        Owners: action.payload,
        OwnersDataPages: currentOwnerPages,
        isFetchingOwners: false,
        isFetchingOwnersFailed: false,
        isFetchingOwnersSuccess: true,
      };
      return { ...state, ...ownersData };
    },
    getFetchingOwnersDataFailed(state, action) {
      state.isFetchingOwnersFailed = true;
      state.isFetchingOwners = false;
      state.errorMessage = action.payload;
      state.isFetchingOwnersSuccess = false;
    },
    getCreatingOwner(state, action) {
      state.errorMessage = {} as ErrorType;
      state.isCreatingOwnerFailed = false;
      state.isCreatingOwnerSuccess = false;
      state.isCreatingOwner = true;
    },
    setCreatingOwner(state, action) {
      state.createdOwner = action.payload;
      state.isCreatingOwnerFailed = false;
      state.isCreatingOwnerSuccess = true;
      state.isCreatingOwner = false;
    },
    getCreatingOwnerFailed(state, action) {
      state.isCreatingOwnerFailed = true;
      state.isCreatingOwnerSuccess = false;
      state.isCreatingOwner = false;
      state.errorMessage = action.payload;
    },
    getUpdatingOwner(state, action) {
      state.errorMessage = {} as ErrorType;
      state.isUpdatingOwnerSuccess = false;
      state.isUpdatingOwnerFailed = false;
      state.isUpdatingOwner = true;
    },
    setUpdatingOwner(state, action) {
      state.isUpdatingOwnerSuccess = true;
      state.isUpdatingOwnerFailed = false;
      state.isUpdatingOwner = false;
    },
    getUpdatingOwnerFailed(state, action) {
      state.isUpdatingOwnerSuccess = false;
      state.isUpdatingOwnerFailed = true;
      state.isUpdatingOwner = false;
      state.errorMessage = action.payload;
    },
    getResetOwnersState(state) {
      return {
        ...state,
        isFetchingOwners: false,
        isFetchingOwnersFailed: false,
        isFetchingOwnersSuccess: false,
        isCreatingOwner: false,
        isCreatingOwnerSuccess: false,
        isCreatingOwnerFailed: false,
        createdOwner: {} as OwnerV2Dto,
        isUpdatingOwner: false,
        isUpdatingOwnerSuccess: false,
        isUpdatingOwnerFailed: false,
        isDeletingOwner: false,
        isDeletingOwnerSuccess: false,
        isDeletingOwnerFailed: false,
        errorMessage: {} as ErrorType,
      };
    },
    getFetchingOwnerDetails(state, action) {
      state.errorMessage = {} as ErrorType;
      state.isFetchingOwnerDetailsFailed = false;
      state.isFetchingOwnerDetails = true;
    },
    setFetchingOwnerDetails(state, action) {
      const ownersData = {
        OwnerDetails: action.payload,
        isFetchingOwnerDetails: false,
        isFetchingOwnerDetailsFailed: false,
        isFetchingOwnerDetailsSuccess: true,
      };
      return { ...state, ...ownersData };
    },
    getFetchingOwnerDetailsFailed(state, action) {
      state.isFetchingOwnerDetailsFailed = true;
      state.isFetchingOwnerDetails = false;
      state.errorMessage = action.payload;
      state.isFetchingOwnerDetailsSuccess = false;
    },
    getDownloading(state, action) {
      state.errorMessage = {} as ErrorType;
      state.isDownloadingFailed = false;
      state.isDownloading = true;
    },
    setDownloading(state) {
      const ownersData = {
        isDownloading: false,
        isDownloadingFailed: false,
        isDownloadingSuccess: true,
      };
      return { ...state, ...ownersData };
    },
    getDownloadingFailed(state, action) {
      state.isDownloadingFailed = true;
      state.isDownloading = false;
      state.errorMessage = action.payload;
      state.isDownloadingSuccess = false;
    },
    getUploadOwnerDocuments(state, action) {
      state.errorMessage = {} as ErrorType;
      state.isUploadingOwnerDocumentsSuccess = false;
      state.isUploadingOwnerDocumentsFailed = false;
      state.isUploadingOwnerDocuments = true;
    },
    setUploadOwnerDocuments(state) {
      const ownersData = {
        isUploadingOwnerDocuments: false,
        isUploadingOwnerDocumentsFailed: false,
        isUploadingOwnerDocumentsSuccess: true,
        errorMessage: {} as ErrorType,
      };
      return { ...state, ...ownersData };
    },
    getUploadOwnerDocumentsFailed(state, action) {
      state.isUploadingOwnerDocumentsFailed = true;
      state.isUploadingOwnerDocuments = false;
      state.errorMessage = action.payload;
      state.isUploadingOwnerDocumentsSuccess = false;
    },
    fetchOwnerDocumentURL(state, action) {
      state.isFetchingOwnerDocumentURL = true;
      state.isFetchingOwnerDocumentURLFailed = false;
      state.isFetchingOwnerDocumentURLSuccess = false;
      state.errorFetchingOwnerDocumentURL = null;
      state.ownerDocumentURL = '';
    },
    setOwnerDocumentURL(state, action) {
      state.isFetchingOwnerDocumentURL = false;
      state.isFetchingOwnerDocumentURLFailed = false;
      state.isFetchingOwnerDocumentURLSuccess = true;
      state.ownerDocumentURL = action.payload;
      state.errorFetchingOwnerDocumentURL = null;
    },
    setFetchOwnerDocumentURLFailed(state, action) {
      state.ownerDocumentURL = '';
      state.isFetchingOwnerDocumentURL = false;
      state.isFetchingOwnerDocumentURLFailed = true;
      state.isFetchingOwnerDocumentURLSuccess = false;
      state.errorFetchingOwnerDocumentURL = action.payload;
    },
    //reset states tabs are changed in the owner details wizard component
    handleOwnerDetailsWizardTabChange(state) {
      state.isFetchingOwnerDocumentURLFailed = false;
      state.isFetchingOwnerDocumentURLSuccess = false;
      state.isDownloadingFailed = false;
      state.isDownloadingSuccess = false;
      //update owner states
      state.isUpdatingOwnerFailed = false;
      state.isUpdatingOwnerSuccess = false;
    },
    //reset states when go back in the owner details wizard component is clicked
    handleOwnerDetailsWizardGoBack(state) {
      //update owner states
      state.isUpdatingOwnerFailed = false;
      state.isUpdatingOwnerSuccess = false;
    },
  },
});

export const {
  getFetchingOwnersData,
  setFetchingOwnersData,
  getFetchingOwnersDataFailed,
  getCreatingOwner,
  setCreatingOwner,
  getCreatingOwnerFailed,
  getUpdatingOwner,
  setUpdatingOwner,
  getUpdatingOwnerFailed,
  getResetOwnersState,
  getFetchingOwnerDetails,
  setFetchingOwnerDetails,
  getFetchingOwnerDetailsFailed,
  getDownloading,
  setDownloading,
  getDownloadingFailed,
  getUploadOwnerDocuments,
  getUploadOwnerDocumentsFailed,
  setUploadOwnerDocuments,
  fetchOwnerDocumentURL,
  setFetchOwnerDocumentURLFailed,
  setOwnerDocumentURL,
  handleOwnerDetailsWizardTabChange,
  handleOwnerDetailsWizardGoBack,
} = OwnersSlice.actions;

export default OwnersSlice.reducer;
export const selectOwners = (state: RootState) => state.ownerModuleState.Owners;
export const selectOwnersCurrentPage = (state: RootState) => state.ownerModuleState.OwnersDataPages;
export const selectOwnersState = (state: RootState) => state.ownerModuleState;
export const selectOwnerDetails = (state: RootState) => state.ownerModuleState.OwnerDetails;
